import { Runtime, ReadonlyArray, pipe } from "fp";
import React from "react";

import * as Data from "~/Data";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import * as Event from "./Event";
import * as Table from "./Table";

export * as Event from "./Event";
export * as Request from "./Request";

export type Audit = {
  readonly revisions: readonly Event.Event[];
  readonly tracker: readonly Event.Event[];
  readonly application: readonly Event.Event[];
};

export const empty = (): Audit => ({
  revisions: [],
  tracker: [],
  application: [],
});

export const sortedEvents = ({
  revisions,
  tracker,
  application,
}: Audit): readonly Event.Event[] =>
  ReadonlyArray.sort(Event.order())([...revisions, ...tracker, ...application]);

export const href = () => "audit-history" as const;

export const use = ({ driver, dateTime }: Log.Identity): Data.Remote<Audit> => {
  const startTime = Time.DateTime.subtract(
    Time.DateTime.toStartOfDay({ dateTime })
  )(12, "hours");
  const stopTime = Time.DateTime.add(Time.DateTime.toStartOfDay({ dateTime }))(
    36,
    "hours"
  );
  return Remote.Query.use<Audit>(
    Log.Audit.Request.Query.key({
      id: Log.Audit.Request.Query.id({ driver, dateTime }),
    }),
    Log.Audit.Request.Query.request({
      driver,
      startTime,
      stopTime,
    }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );
};

export const useSortedEventsFromActiveLog = (
  route: Log.Route
): Data.Remote<readonly Event.Event[]> =>
  pipe(
    use(route),
    Data.Remote.getOrElse(empty),
    sortedEvents,
    Data.Remote.success
  );

export const Component = (route: Log.Route) => (
  <Layout.Section.Component>
    <Layout.Block.Component all>
      <Typography.H2.Component id={href()}>
        Audit History
      </Typography.H2.Component>
    </Layout.Block.Component>
    <Table.Component {...route} />
  </Layout.Section.Component>
);

export namespace Response {
  export const runtime = () =>
    Runtime.strict({
      log_revisions: Runtime.array(Event.Response.runtime()),
      tracker_logs: Runtime.array(Event.Response.runtime()),
      application_logs: Runtime.array(Event.Response.runtime()),
    });

  export const decode =
    (driver: Data.Reference.Reference) =>
    (response: Runtime.ToType<typeof runtime>): Audit => ({
      revisions: ReadonlyArray.map(Event.Response.decode(driver))(
        response.log_revisions
      ),
      tracker: ReadonlyArray.map(Event.Response.decode(driver))(
        response.tracker_logs
      ),
      application: ReadonlyArray.map(Event.Response.decode(driver))(
        response.application_logs
      ),
    });
}
